import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Star } from "lucide-react";
import { CATEGORY_META, fmtMarketCap, fmtPct } from "../lib/api";

const COLUMNS = [
    { key: "ticker",        label: "Ticker",     align: "left",  sticky: true },
    { key: "name",          label: "Name",       align: "left" },
    { key: "category",      label: "Cat.",       align: "left" },
    { key: "sector",        label: "Sector",     align: "left", hideOn: "md" },
    { key: "yield",         label: "Yield",      align: "right" },
    { key: "payout_ratio",  label: "Payout",     align: "right" },
    { key: "dgr_5y",        label: "DGR 5Y",     align: "right" },
    { key: "cagr_5y",       label: "CAGR 5Y",    align: "right" },
    { key: "growth_streak", label: "Streak",     align: "right" },
    { key: "pe",            label: "P/E",        align: "right", hideOn: "md" },
    { key: "market_cap",    label: "Mkt Cap",    align: "right", hideOn: "md" },
];

export default function ScreenerTable({ rows, sort, onSort, watchlist, onToggleWatch }) {
    return (
        <div className="rounded-2xl border border-border bg-background overflow-hidden" data-testid="screener-table">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b border-border">
                        <tr>
                            <th className="w-10 px-3 py-3"></th>
                            {COLUMNS.map((c) => (
                                <th
                                    key={c.key}
                                    onClick={() => onSort(c.key)}
                                    className={`sort-th px-3 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${
                                        c.align === "right" ? "text-right" : "text-left"
                                    } ${c.hideOn === "md" ? "hidden md:table-cell" : ""}`}
                                    data-testid={`sort-${c.key}`}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {c.label}
                                        <SortIcon active={sort.field === c.key} dir={sort.dir} />
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={COLUMNS.length + 1} className="text-center py-16 text-muted-foreground">
                                    No stocks match your filters. Try resetting.
                                </td>
                            </tr>
                        )}
                        {rows.map((row, idx) => {
                            const isWatched = watchlist?.has(row.ticker);
                            const catMeta = CATEGORY_META[row.category];
                            return (
                                <tr
                                    key={row.ticker}
                                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors animate-fade-up"
                                    style={{ animationDelay: `${Math.min(idx * 15, 300)}ms` }}
                                    data-testid={`row-${row.ticker}`}
                                >
                                    <td className="px-3 py-3">
                                        <button
                                            onClick={() => onToggleWatch(row.ticker, isWatched)}
                                            className="text-muted-foreground hover:text-amber-500 transition-colors"
                                            aria-label="Toggle watchlist"
                                            data-testid={`watch-toggle-${row.ticker}`}
                                        >
                                            <Star className={`h-4 w-4 ${isWatched ? "fill-amber-400 text-amber-500" : ""}`} />
                                        </button>
                                    </td>
                                    <td className="px-3 py-3">
                                        <Link to={`/stock/${row.ticker}`} className="font-semibold font-heading tracking-tight hover:text-primary" data-testid={`ticker-link-${row.ticker}`}>
                                            {row.ticker}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-3 text-foreground/90 max-w-[220px] truncate">{row.name}</td>
                                    <td className="px-3 py-3">
                                        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md ${catMeta?.cls || ""}`}>
                                            {catMeta?.label || row.category}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-muted-foreground hidden md:table-cell">{row.sector}</td>
                                    <td className="px-3 py-3 text-right tabular-nums font-medium">{fmtPct(row.yield)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums">{fmtPct(row.payout_ratio, 0)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700 font-medium">{fmtPct(row.dgr_5y, 1)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{fmtPct(row.cagr_5y, 1)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums font-semibold">{row.growth_streak}y</td>
                                    <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell">{row.pe?.toFixed(1)}</td>
                                    <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell">{fmtMarketCap(row.market_cap)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SortIcon({ active, dir }) {
    if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return dir === "asc" ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
}
